import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../../utils/analytics';

const KEY = 'tlk_age_ok_until';
const BLOCK = 'tlk_age_blocked';
const DAYS = 30;
const EXIT_URL = '/access-denied';

const readOk = (storageKey = KEY) => {
    try {
        if (sessionStorage.getItem(BLOCK)) return false;
        return Number(localStorage.getItem(storageKey)) > Date.now();
    } catch { return false; }
};
const readBlocked = () => {
    try { return !!sessionStorage.getItem(BLOCK); } catch { return false; }
};

const AgeGate = ({ children, storageKey = KEY }) => {
    const [ok, setOk] = useState(() => readOk(storageKey));
    const blocked = readBlocked();
    const navigator = useNavigate();
    // lock page scroll while the gate is up
    useEffect(() => {
        if (ok || blocked) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        trackEvent('age_gate_open', { source: 'age_gate' });
        return () => { document.body.style.overflow = prev; };
    }, [ok, blocked]);

    useEffect(() => {
        if (blocked) navigator(EXIT_URL);
    }, [blocked]);

    if (blocked) return null;

    const confirm = () => {
        try {
            const until = String(Date.now() + DAYS * 864e5);
            localStorage.setItem(storageKey, until);
            // A pass on the creator link also unlocks the rest of the site.
            if (storageKey !== KEY) localStorage.setItem(KEY, until);
        } catch { }
        setOk(true);
        trackEvent('age_gate_confirm', { source: storageKey === KEY ? 'age_gate' : 'magic_link' });
    };
    const deny = () => {
        try {
            sessionStorage.setItem(BLOCK, '1');
            localStorage.removeItem(KEY);
            localStorage.removeItem(storageKey);
        } catch { }
        navigator(EXIT_URL);
    };

    return (
        <>
            {/* children are NOT mounted until confirmed, so restricted content never hits the DOM */}
            {ok && children}
            <AnimatePresence>
                {!ok && (
                    <motion.div
                        className="pwa-install-overlay"
                        style={{ zIndex: 99999 }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            role="alertdialog"
                            aria-modal="true"
                            aria-labelledby="age-gate-title"
                            aria-describedby="age-gate-desc"
                            className="pwa-install-modal"
                            initial={{ y: 28, scale: 0.95, opacity: 0 }}
                            animate={{ y: 0, scale: 1, opacity: 1 }}
                            exit={{ y: 20, scale: 0.96, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                        >
                            <div className="pwa-install-glow" />
                            <div className="pwa-install-content">
                                {/* no close button, no backdrop dismiss: must choose */}
                                <div className="pwa-install-badge">18+ Only</div>
                                <h2 id="age-gate-title" className="pwa-install-heading">
                                    Are you 18 or older?
                                </h2>
                                <p id="age-gate-desc" className="pwa-install-copy">
                                    TLK is an AI companion experience for adults 18+. Conversations may include mature themes.
                                </p>

                                <div className="pwa-age-disclaimer">
                                    By continuing, you confirm that you are at least 18 and meet the minimum age requirement in your jurisdiction, and agree to our{' '}
                                    <a href="/user/terms" target="_blank" rel="noopener noreferrer">Terms</a>{' '}
                                    and{' '}
                                    <a href="/user/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                                </div>

                                <div className="pwa-install-actions">
                                    <button className="pwa-btn-secondary" onClick={deny}>
                                        I'm under 18
                                    </button>
                                    <button className="pwa-btn-primary" onClick={confirm}>
                                        I'm 18 or older
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default AgeGate;